import React, { useCallback, useEffect, useState } from 'react';
import T from 'prop-types';
import { Chip, FormControl, Input, InputAdornment, InputLabel, MenuItem, Select } from '@mui/material';
import { NotifyIcon } from 'web-components';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import { CustomCheckbox, getStyles, useStyles } from './styles';

function MachineFilter({
  labelFilter,
  optionsObj,
  isCheckbox,
  isClonable,
  isSearchIcon,
  clear,
  setClear,
  handleFilter,
  setInitStates
}) {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const [selectedValue, setSelectedValue] = useState(isCheckbox ? [] : '');

  const clearFilter = useCallback(() => {
    setSelectedValue(isCheckbox ? [] : '');
    setInitStates();
    setClear(false);
  }, [setClear, setInitStates, setSelectedValue, isCheckbox]);

  useEffect(() => {
    if (clear) {
      clearFilter();
    }
  }, [clear, clearFilter]);

  useEffect(() => {
    handleFilter(selectedValue);
  }, [handleFilter, selectedValue]);

  const handleChange = event => {
    setSelectedValue(event.target.value);
  };
  const handleDelete = value => {
    const newState = selectedValue.filter(item => item !== value);
    setSelectedValue(newState);
  };

  const IconFilter = () => <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />;
  const IconSearch = () => (
    <InputAdornment position="end" sx={{ pb: 1.5 }}>
      <SearchIcon color="primary" />
    </InputAdornment>
  );

  const returnLabel = value => {
    const searchLabel = optionsObj.filter(item => value === item.value);
    return searchLabel[0].label;
  };

  const buildOptionObj = () => {
    if (isCheckbox) {
      return (
        <>
          <InputLabel id="multiple-chip-label" sx={{ color: '#7A7D85', opacity: 0.6 }}>
            {labelFilter}
          </InputLabel>
          <Select
            labelId="multiple-checkbox-label"
            id="multiple-checkbox"
            multiple
            value={selectedValue}
            onChange={handleChange}
            IconComponent={IconFilter}
            input={<Input id="select-multiple-chip" />}
            renderValue={selected => (
              <div className={classes.chips}>
                {selected.map(value => (
                  <Chip
                    key={value}
                    label={returnLabel(value)}
                    clickable
                    deleteIcon={
                      <NotifyIcon fontSize="default" iconName="cancel" onMouseDown={event => event.stopPropagation()} />
                    }
                    className={classes.chip}
                    classes={{ deleteIcon: classes.deleteIcon }}
                    size="small"
                    color="primary"
                    onDelete={() => handleDelete(value)}
                  />
                ))}
              </div>
            )}
          >
            {optionsObj.map(item => (
              <MenuItem key={item.value} value={item.value} style={getStyles(item, selectedValue, theme)}>
                <CustomCheckbox checked={selectedValue.indexOf(item.value) > -1} color="primary" />
                {t(item.label)}
              </MenuItem>
            ))}
          </Select>
        </>
      );
    }
    return (
      <>
        <InputLabel id="multiple-chip-label" sx={{ color: '#7A7D85', opacity: 0.6 }}>
          {labelFilter}
        </InputLabel>
        <Select
          labelId="simple-select"
          id="simple-select"
          value={selectedValue}
          onChange={handleChange}
          IconComponent={isSearchIcon ? IconSearch : IconFilter}
          input={<Input id="select-simple" />}
        >
          <MenuItem value="" disabled style={{ backgroundColor: 'transparent' }} />
          {optionsObj.map(item => (
            <MenuItem key={item.value} value={item.value} style={getStyles(item, selectedValue, theme)}>
              {t(item.label)}
            </MenuItem>
          ))}
        </Select>
      </>
    );
  };

  const buildOptionText = () => (
    <>
      <Input
        id="simple-text"
        variant="standard"
        placeholder={labelFilter}
        value={selectedValue}
        onChange={handleChange}
        sx={{ py: 1, px: 1, background: '#FFFFFF' }}
        endAdornment={
          <InputAdornment position="end">
            {isClonable ? (
              <SearchIcon color="primary" />
            ) : (
              <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />
            )}
          </InputAdornment>
        }
      />
    </>
  );

  return (
    <FormControl className={classes.formControl}>
      {optionsObj.length > 0 ? buildOptionObj() : buildOptionText()}
    </FormControl>
  );
}

MachineFilter.propTypes = {
  labelFilter: T.string.isRequired,
  optionsObj: T.arrayOf(
    T.shape({
      label: T.string,
      value: T.string
    })
  ),
  isCheckbox: T.bool,
  isClonable: T.bool,
  isSearchIcon: T.bool,
  clear: T.bool,
  setClear: T.func,
  handleFilter: T.func,
  setInitStates: T.func
};

MachineFilter.defaultProps = {
  isCheckbox: true,
  isClonable: false,
  isSearchIcon: false,
  clear: false,
  optionsObj: [],
  setClear: () => null,
  handleFilter: () => null,
  setInitStates: () => null
};

export { MachineFilter };
